import { supabase } from './supabase';

async function selectWithCount(table, filterBuilder) {
  let query = supabase.from(table).select('*', { count: 'exact', head: true });
  if (typeof filterBuilder === 'function') {
    query = filterBuilder(query);
  }
  const { count, error } = await query;
  if (error) {
    throw error;
  }
  return count ?? 0;
}

async function logAdminActivity({
  adminUserId,
  activityType,
  entityType,
  entityId,
  description,
  metadata,
}) {
  if (!adminUserId || !activityType) {
    return;
  }
  const { error } = await supabase
    .from('admin_activity_logs')
    .insert({
      admin_user_id: adminUserId,
      activity_type: activityType,
      entity_type: entityType || null,
      entity_id: entityId || null,
      description: description || null,
      metadata: metadata || {},
    });
  if (error) {
    console.error('Failed to log admin activity', error);
  }
}

export async function fetchDashboardSummary() {
  try {
    const [totalCaterers, unverifiedCaterers, eliteCaterers, totalCustomers, totalOrders, inProgressOrders, revenueQuery] = await Promise.all([
      selectWithCount('caterers'),
      selectWithCount('caterers', (query) => query.eq('verification_tier', 'none')),
      selectWithCount('caterers', (query) => query.eq('verification_tier', 'elite')),
      selectWithCount('customer_profiles'),
      selectWithCount('orders'),
      selectWithCount('orders', (query) => query.in('status', ['pending', 'in_progress', 'confirmed'])),
      supabase
        .from('platform_revenue')
        .select('amount')
        .order('created_at', { ascending: false })
        .limit(12),
    ]);

    if (revenueQuery.error) {
      throw revenueQuery.error;
    }

    const revenueResult = revenueQuery;
    const revenueSum = Array.isArray(revenueResult?.data)
      ? revenueResult.data.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
      : 0;

    return {
      totalCaterers,
      pendingCaterers: unverifiedCaterers,
      suspendedCaterers: eliteCaterers,
      totalCustomers,
      totalOrders,
      inProgressOrders,
      trailingRevenue: revenueSum,
    };
  } catch (error) {
    console.error('Failed to load dashboard summary', error);
    throw error;
  }
}

export async function fetchRecentOrders(limit = 8) {
  const { data, error } = await supabase
    .from('orders')
    .select(
      `id, status, total_amount, created_at, delivery_date, delivery_location,
       caterer:caterers(id, business_name),
       customer:users(id, email),
       event:events(event_date, estimated_guests)`
    )
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Failed to load recent orders', error);
    throw error;
  }

  return (data ?? []).map((o) => ({
    ...o,
    caterer: o.caterer ? { name: o.caterer.business_name, ...o.caterer } : null,
    guests: o.event?.estimated_guests ?? null,
  }));
}

export async function fetchTopCaterers(limit = 5) {
  const { data, error } = await supabase
    .from('caterer_stats')
    .select('total_revenue, completed_orders, caterer:caterers(id, business_name, location, verification_tier)')
    .order('total_revenue', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Failed to load top caterers', error);
    throw error;
  }

  return (data ?? []).map((row) => ({
    id: row.caterer?.id,
    name: row.caterer?.business_name,
    business_name: row.caterer?.business_name,
    location: row.caterer?.location,
    status: row.caterer?.verification_tier ?? 'active',
    total_revenue: row.total_revenue,
    total_orders: row.completed_orders,
    completed_orders: row.completed_orders,
  }));
}

export async function fetchCaterers({ status, search, limit = 25, offset = 0, fetchAll = false, withCount = false } = {}) {
  const selectClause =
    `id, user_id, business_name, email, phone, verification_tier, is_blocked, blocked_at, blocked_reason, is_suspect, suspect_at, suspect_reason, bank_verified, recipient_code, created_at, location, logo_url, hero_banner_url, claimed, claim_status, caterer_stats(total_orders, total_revenue, average_rating)`;

  const applyFilters = (query) => {
    let next = query;
    if (status && status !== 'all') {
      if (status === 'blocked') {
        next = next.eq('is_blocked', true);
      } else {
        next = next.eq('verification_tier', status);
      }
    }
    if (search && search.trim()) {
      const q = search.trim();
      next = next.or(`business_name.ilike.%${q}%,email.ilike.%${q}%`);
    }
    return next;
  };

  let rows = [];

  if (fetchAll) {
    // Supabase projects often cap rows per request (commonly 1000). Page until exhausted.
    const pageSize = 1000;
    let pageOffset = Math.max(0, offset);

    while (true) {
      let pageQuery = supabase
        .from('caterers')
        .select(selectClause)
        .order('created_at', { ascending: false })
        .range(pageOffset, pageOffset + pageSize - 1);

      pageQuery = applyFilters(pageQuery);
      const { data, error } = await pageQuery;
      if (error) {
        console.error('Failed to load caterers', error);
        throw error;
      }

      const batch = data ?? [];
      rows = rows.concat(batch);

      if (batch.length < pageSize) break;
      pageOffset += pageSize;
    }
  } else {
    let query = supabase
      .from('caterers')
      .select(selectClause, withCount ? { count: 'exact' } : undefined)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    query = applyFilters(query);
    const { data, error, count } = await query;
    if (error) {
      console.error('Failed to load caterers', error);
      throw error;
    }
    rows = data ?? [];
    if (withCount) {
      // Keep total count for proper server-side pagination controls.
      rows._meta = { total: count ?? 0 };
    }
  }

  // For readiness, fetch simple menu counts so ops can see who still needs to set up menus.
  if (!rows.length) return rows;

  const catererIds = rows.map((c) => c.id);
  const { data: menuCounts, error: menuError } = await supabase
    .from('menu_items')
    .select('caterer_id, id')
    .in('caterer_id', catererIds);

  const countsByCaterer = new Map();
  if (!menuError && Array.isArray(menuCounts)) {
    for (const item of menuCounts) {
      if (!catererIds.includes(item.caterer_id)) continue;
      countsByCaterer.set(item.caterer_id, (countsByCaterer.get(item.caterer_id) || 0) + 1);
    }
  }

  const normalized = rows.map((c) => ({
    ...c,
    has_logo: !!c.logo_url,
    has_banner: !!c.hero_banner_url,
    menu_item_count: countsByCaterer.get(c.id) || 0,
  }));

  if (withCount && rows._meta) {
    return { rows: normalized, total: rows._meta.total };
  }

  return normalized;
}

export async function updateCatererStatus(catererId, verificationTier, adminUserId) {
  const { error } = await supabase
    .from('caterers')
    .update({ verification_tier: verificationTier, updated_at: new Date().toISOString() })
    .eq('id', catererId);

  if (error) {
    console.error('Failed to update caterer verification tier', error);
    throw error;
  }

  // Log admin activity
  await logAdminActivity({
    adminUserId,
    activityType: 'caterer_verification_updated',
    entityType: 'caterer',
    entityId: catererId,
    description: `Verification tier updated to ${verificationTier}`,
    metadata: { verification_tier: verificationTier },
  });

  // Notify caterer via in-app message (messages table)
  try {
    const { data: caterer } = await supabase
      .from('caterers')
      .select('user_id, business_name')
      .eq('id', catererId)
      .maybeSingle();

    if (caterer?.user_id && adminUserId) {
      const subject = 'Your verification tier was updated';
      const body =
        `Hi ${caterer.business_name || 'there'},\n\n` +
        `Your Mrs Cater verification tier has been updated to "${verificationTier}".\n\n` +
        `If you have any questions, you can reply to this message.\n\n` +
        `— Mrs Cater Admin`;

      await supabase.from('messages').insert({
        sender_id: adminUserId,
        recipient_id: caterer.user_id,
        subject,
        body,
        related_order_id: null,
        related_event_id: null,
      });
    }
  } catch (e) {
    // Non-blocking – messaging failure should not break admin action
    console.warn('Failed to send verification-tier message to caterer', e);
  }
}

/**
 * Fetch customers: users who have placed orders (and optionally have customer_profiles).
 * Derives from orders so we show everyone who has ordered, even without a profile.
 */
export async function fetchCustomers({ search, limit = 500, offset = 0 } = {}) {
  const { data: orders, error: ordersError } = await supabase
    .from('orders')
    .select('customer_id, created_at, total_amount, status')
    .order('created_at', { ascending: false })
    .limit(10000);

  if (ordersError) {
    console.error('Failed to load orders for customers', ordersError);
    throw ordersError;
  }

  const byCustomer = new Map();
  for (const o of orders ?? []) {
    if (!o.customer_id) continue;
    const c = byCustomer.get(o.customer_id);
    const orderTotal = Number(o.total_amount) || 0;
    const completed = o.status === 'completed';
    if (!c) {
      byCustomer.set(o.customer_id, {
        firstOrderAt: o.created_at,
        orderCount: 1,
        totalSpent: completed ? orderTotal : 0,
      });
    } else {
      c.orderCount += 1;
      if (completed) c.totalSpent += orderTotal;
    }
  }

  let ids = [...byCustomer.keys()];
  const sortOrder = (orders ?? []).filter((o) => o.customer_id);
  ids.sort((a, b) => {
    const aAt = byCustomer.get(a)?.firstOrderAt ?? '';
    const bAt = byCustomer.get(b)?.firstOrderAt ?? '';
    return new Date(bAt) - new Date(aAt);
  });

  if (ids.length === 0) {
    return [];
  }

  const { data: users, error: usersError } = await supabase
    .from('users')
    .select('id, email')
    .in('id', ids);

  if (usersError) {
    console.error('Failed to load users for customers', usersError);
    throw usersError;
  }

  const usersMap = new Map((users ?? []).map((u) => [u.id, u]));

  const { data: profiles } = await supabase
    .from('customer_profiles')
    .select('user_id, full_name, phone, created_at')
    .in('user_id', ids);

  const profilesMap = new Map((profiles ?? []).map((p) => [p.user_id, p]));

  const { data: events } = await supabase
    .from('events')
    .select('customer_id')
    .in('customer_id', ids);
  const eventsByCustomer = new Map();
  for (const e of events ?? []) {
    if (!e.customer_id) continue;
    eventsByCustomer.set(e.customer_id, (eventsByCustomer.get(e.customer_id) ?? 0) + 1);
  }

  let rows = ids.map((id) => {
    const u = usersMap.get(id);
    const p = profilesMap.get(id);
    const agg = byCustomer.get(id);
    const eventCount = eventsByCustomer.get(id) ?? 0;
    return {
      id,
      full_name: p?.full_name ?? null,
      email: u?.email ?? null,
      phone: p?.phone ?? null,
      total_events_organized: eventCount,
      total_spent: agg?.totalSpent ?? 0,
      created_at: p?.created_at ?? agg?.firstOrderAt ?? null,
    };
  });

  if (search && search.trim()) {
    const q = search.trim().toLowerCase();
    rows = rows.filter(
      (r) =>
        (r.full_name ?? '').toLowerCase().includes(q) ||
        (r.email ?? '').toLowerCase().includes(q)
    );
  }

  const start = Math.max(0, offset);
  const end = Math.min(rows.length, start + limit);
  return rows.slice(start, end);
}

export async function fetchOrders({ status, includeArchived = false, limit = 50, offset = 0 } = {}) {
  let query = supabase
    .from('orders')
    .select(
      `id, status, total_amount, created_at, delivery_date, delivery_location, payment_status, payment_reference, escrow_id, archived_at,
       caterer:caterers(business_name, email),
       customer:users(id, email),
       event:events(event_date, estimated_guests),
       escrow:order_escrow!orders_escrow_id_fkey(escrow_status, caterer_net_amount)`
    )
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (status && status !== 'all') {
    query = query.eq('status', status);
  }
  if (!includeArchived) {
    query = query.is('archived_at', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Failed to load orders', error);
    throw error;
  }

  return (data ?? []).map((o) => ({
    ...o,
    event_date: o.event?.event_date ?? o.delivery_date,
    guests: o.event?.estimated_guests ?? null,
  }));
}

/** Archive all completed orders that are not yet archived. */
export async function archiveCompletedOrders(adminUserId) {
  const { data: ids } = await supabase
    .from('orders')
    .select('id')
    .eq('status', 'completed')
    .is('archived_at', null);
  if (!ids?.length) return { archived: 0 };
  const { error } = await supabase
    .from('orders')
    .update({ archived_at: new Date().toISOString() })
    .eq('status', 'completed')
    .is('archived_at', null);
  if (error) throw error;
  return { archived: ids.length };
}

export async function fetchOrderById(orderId) {
  const { data, error } = await supabase
    .from('orders')
    .select(
      `*,
       caterer:caterers(id, business_name, email, phone),
       customer:users(id, email),
       event:events(id, title, event_type, event_date, estimated_guests, location, special_requirements),
       escrow:order_escrow!orders_escrow_id_fkey(id, escrow_status, gross_amount, commission_amount, caterer_net_amount, payment_reference, payment_status),
       order_items(id, name, quantity, unit_price, platform_markup_amount, subtotal, markup_total, category)`
    )
    .eq('id', orderId)
    .single();

  if (error) {
    console.error('Failed to load order', error);
    throw error;
  }

  return {
    ...data,
    event_date: data.event?.event_date ?? data.delivery_date,
    guests: data.event?.estimated_guests ?? null,
  };
}

export async function fetchPlatformRevenueHistory(limit = 12) {
  const { data, error } = await supabase
    .from('platform_revenue')
    .select('id, amount, revenue_stream, description, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Failed to load platform revenue', error);
    throw error;
  }

  return data ?? [];
}

/** Platform profit by stream: commission, menu_markup, verification_fee, sponsored_placement. */
export async function fetchPlatformRevenueByStream() {
  const { data, error } = await supabase
    .from('platform_revenue')
    .select('revenue_stream, amount');
  if (error) throw error;
  const byStream = { commission: 0, menu_markup: 0, verification_fee: 0, sponsored_placement: 0 };
  for (const row of data ?? []) {
    const key = row.revenue_stream || 'commission';
    if (byStream[key] !== undefined) byStream[key] += Number(row.amount) || 0;
  }
  return byStream;
}

// ============================================================================
// ESCROW OPERATIONS
// ============================================================================

export async function fetchAllEscrows({ status, limit = 100, offset = 0 } = {}) {
  let query = supabase
    .from('order_escrow')
    .select(`
      *,
      order:orders!order_escrow_order_id_fkey(id, status, total_amount, delivery_date),
      caterer:caterers(id, business_name, email),
      customer:users!order_escrow_customer_id_fkey(id, email)
    `)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (status && status !== 'all') {
    query = query.eq('escrow_status', status);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Failed to load escrows', error);
    throw error;
  }
  return data ?? [];
}

/**
 * Manually release escrow funds. Requires the admin user's access token; the
 * edge function verifies the caller is an admin before releasing.
 */
export async function releaseEscrowFunds(escrowId, adminUserId, reason, accessToken) {
  if (!accessToken) {
    throw new Error('Admin session required to release escrow. Please sign in again.');
  }
  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/release-escrow`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      escrowId,
      verifiedBy: adminUserId,
      verificationProof: { admin_release: true, reason }
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to release escrow' }));
    throw new Error(error.error || 'Failed to release escrow');
  }

  return await response.json();
}

/**
 * Admin-only: clear a disputed escrow so it can be released.
 */
export async function resolveOrderDispute(escrowId, adminUserId, resolutionReason, accessToken) {
  if (!accessToken) {
    throw new Error('Admin session required to resolve disputes. Please sign in again.');
  }

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/resolve-order-dispute`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      escrowId,
      resolvedBy: adminUserId,
      resolutionReason: resolutionReason || 'Resolved by admin',
      reason: resolutionReason || 'Resolved by admin',
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to resolve dispute' }));
    throw new Error(error.error || 'Failed to resolve dispute');
  }

  return await response.json();
}

export async function getEscrowById(escrowId) {
  const { data, error } = await supabase
    .from('order_escrow')
    .select(`
      *,
      order:orders!order_escrow_order_id_fkey(*),
      caterer:caterers(*),
      customer:users!order_escrow_customer_id_fkey(*),
      history:escrow_status_history(*)
    `)
    .eq('id', escrowId)
    .single();

  if (error) {
    console.error('Failed to load escrow', error);
    throw error;
  }
  return data;
}

// ============================================================================
// PAYMENT OPERATIONS
// ============================================================================

export async function fetchAllPayments({ status, limit = 100, offset = 0 } = {}) {
  let query = supabase
    .from('order_escrow')
    .select(`
      id,
      payment_reference,
      payment_status,
      payment_method,
      payment_gateway,
      gross_amount,
      created_at,
      escrowed_at,
      order:orders!order_escrow_order_id_fkey(id, status, total_amount, delivery_date),
      caterer:caterers(id, business_name),
      customer:users!order_escrow_customer_id_fkey(id, email)
    `)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (status && status !== 'all') {
    query = query.eq('payment_status', status);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Failed to load payments', error);
    throw error;
  }
  return data ?? [];
}

export async function fetchFailedPayments(limit = 50) {
  const { data, error } = await supabase
    .from('order_escrow')
    .select(`
      id,
      payment_reference,
      payment_status,
      payment_method,
      gross_amount,
      created_at,
      order:orders!order_escrow_order_id_fkey(id, status, total_amount),
      caterer:caterers(id, business_name),
      customer:users!order_escrow_customer_id_fkey(id, email)
    `)
    .eq('payment_status', 'failed')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Failed to load failed payments', error);
    throw error;
  }
  return data ?? [];
}

// ============================================================================
// BID OPERATIONS
// ============================================================================

export async function fetchAllBids({ status, limit = 100, offset = 0 } = {}) {
  let query = supabase
    .from('bids')
    .select(`
      *,
      event:events(id, title, event_type, estimated_guests, event_date, location, budget_min, budget_max, customer_id, customer:users!events_customer_id_fkey(id, email)),
      caterer:caterers(id, business_name, email, verification_tier)
    `)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (status && status !== 'all') {
    query = query.eq('status', status);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Failed to load bids', error);
    throw error;
  }
  return (data ?? []).map((b) => ({ ...b, customer: b.event?.customer ?? null }));
}

export async function fetchAcceptedBids(limit = 50) {
  const { data, error } = await supabase
    .from('bids')
    .select(`
      *,
      event:events(id, title, event_type, estimated_guests, event_date, location, customer:users!events_customer_id_fkey(id, email)),
      caterer:caterers(id, business_name, email),
      order:orders(id, status, total_amount)
    `)
    .eq('status', 'accepted')
    .order('accepted_date', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Failed to load accepted bids', error);
    throw error;
  }
  return (data ?? []).map((b) => ({ ...b, customer: b.event?.customer ?? null }));
}

// ============================================================================
// EVENTS / SPECIAL REQUESTS
// ============================================================================

const EVENTS_FULL_SELECT = `id, title, event_type, event_date, estimated_guests, location, budget_min, budget_max,
  special_requirements, status, is_special_request, request_fee, request_fee_paid,
  request_fee_refunded, request_fee_payment_ref, request_fee_paid_at, created_at,
  customer:users(id, email)`;

const EVENTS_MINIMAL_SELECT = `id, title, event_type, event_date, estimated_guests, location, budget_min, budget_max,
  special_requirements, status, created_at, customer:users(id, email)`;

export async function fetchEvents({ isSpecialRequest, status, limit = 100, offset = 0 } = {}) {
  const run = async (select) => {
    let query = supabase
      .from('events')
      .select(select)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    if (isSpecialRequest === true && select === EVENTS_FULL_SELECT) {
      query = query.eq('is_special_request', true);
    }
    if (status && status !== 'all') {
      query = query.eq('status', status);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  };

  try {
    return await run(EVENTS_FULL_SELECT);
  } catch (e) {
    const minimal = await run(EVENTS_MINIMAL_SELECT);
    return minimal.map((r) => ({ ...r, is_special_request: null, request_fee: null, request_fee_paid: null, request_fee_refunded: null }));
  }
}

// ============================================================================
// PAYOUTS
// ============================================================================

export async function fetchPayouts({ status, limit = 100, offset = 0 } = {}) {
  let query = supabase
    .from('payouts')
    .select(
      `id, amount, status, payout_method, transaction_id, transfer_code, transfer_status,
       requested_date, processed_at, completed_at, failed_reason, metadata, created_at,
       caterer:caterers(id, business_name, email, bank_verified, recipient_code)`
    )
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (status && status !== 'all') {
    query = query.eq('status', status);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Failed to load payouts', error);
    throw error;
  }
  return data ?? [];
}

/** Call Edge Function to finalize a payout with the OTP Paystack sent to the platform email. */
export async function finalizePayoutWithOtp(payoutId, otp) {
  const { data, error } = await supabase.functions.invoke('finalize-payout-otp', {
    body: { payoutId, otp },
  });
  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || 'Finalize failed');
  return data;
}

// ============================================================================
// SYSTEM MONITORING
// ============================================================================

export async function fetchSystemMetrics() {
  try {
    const [
      totalEscrows,
      pendingEscrows,
      releasedEscrows,
      totalBids,
      pendingBids,
      acceptedBids,
      totalPayments,
      failedPayments,
      totalRevenue,
      escrowBalance
    ] = await Promise.all([
      selectWithCount('order_escrow'),
      selectWithCount('order_escrow', (q) => q.eq('escrow_status', 'escrowed')),
      selectWithCount('order_escrow', (q) => q.eq('escrow_status', 'released')),
      selectWithCount('bids'),
      selectWithCount('bids', (q) => q.eq('status', 'pending')),
      selectWithCount('bids', (q) => q.eq('status', 'accepted')),
      selectWithCount('order_escrow', (q) => q.eq('payment_status', 'completed')),
      selectWithCount('order_escrow', (q) => q.eq('payment_status', 'failed')),
      supabase.from('platform_revenue').select('amount'),
      supabase.from('order_escrow')
        .select('caterer_net_amount')
        .eq('escrow_status', 'escrowed')
    ]);

    const revenueSum = Array.isArray(totalRevenue?.data)
      ? totalRevenue.data.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
      : 0;

    const escrowTotal = Array.isArray(escrowBalance?.data)
      ? escrowBalance.data.reduce((sum, item) => sum + (Number(item.caterer_net_amount) || 0), 0)
      : 0;

    return {
      escrows: {
        total: totalEscrows,
        pending: pendingEscrows,
        released: releasedEscrows,
        balance: escrowTotal
      },
      bids: {
        total: totalBids,
        pending: pendingBids,
        accepted: acceptedBids
      },
      payments: {
        total: totalPayments,
        failed: failedPayments
      },
      revenue: revenueSum
    };
  } catch (error) {
    console.error('Failed to load system metrics', error);
    throw error;
  }
}

// ============================================================================
// AI ANALYTICS INSIGHTS
// ============================================================================

/**
 * Call AI analytics Edge Function for operator insights.
 * tool: 'overview' | 'ordering_patterns' | 'time_of_orders' | 'menu_views' | 'caterer_performance'
 */
export async function fetchAiAnalyticsInsight(tool = 'overview') {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!baseUrl) {
    throw new Error('VITE_SUPABASE_URL is not configured');
  }
  const url = `${baseUrl.replace(/\/$/, '')}/functions/v1/ai-analytics-insights`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      // Some Supabase setups require an Authorization bearer even for Edge Functions.
      // We reuse the anon key here so the gateway accepts the request.
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ tool }),
  });
  if (!res.ok) {
    let errDetail = '';
    try {
      const errJson = await res.json();
      errDetail = errJson?.error || '';
    } catch {
      // ignore
    }
    throw new Error(errDetail || `Failed to fetch AI analytics insight (${res.status})`);
  }
  const json = await res.json();
  return json?.insight || '';
}

// ============================================================================
// CATERER MANAGEMENT
// ============================================================================

/** Block caterer: hide from customers, show blocked screen on caterer app. Does not change verification_tier. */
export async function blockCaterer(catererId, reason, adminUserId) {
  const { error } = await supabase
    .from('caterers')
    .update({
      is_blocked: true,
      blocked_at: new Date().toISOString(),
      blocked_reason: reason || null,
      updated_at: new Date().toISOString()
    })
    .eq('id', catererId);

  if (error) {
    console.error('Failed to block caterer', error);
    throw error;
  }
  await logAdminActivity({
    adminUserId,
    activityType: 'caterer_blocked',
    entityType: 'caterer',
    entityId: catererId,
    description: 'Caterer blocked',
    metadata: { reason },
  });

  // Notify caterer via in-app message
  try {
    const { data: caterer } = await supabase
      .from('caterers')
      .select('user_id, business_name')
      .eq('id', catererId)
      .maybeSingle();

    if (caterer?.user_id && adminUserId) {
      const subject = 'Your Mrs Cater account has been blocked';
      const body =
        `Hi ${caterer.business_name || 'there'},\n\n` +
        `Your caterer account has been blocked by the Mrs Cater admin team.\n\n` +
        `Reason: ${reason || 'No reason provided.'}\n\n` +
        `You will not be able to access the dashboard or perform any actions while blocked. ` +
        `Please contact support if you believe this is an error.\n\n` +
        `— Mrs Cater Admin`;

      await supabase.from('messages').insert({
        sender_id: adminUserId,
        recipient_id: caterer.user_id,
        subject,
        body,
        related_order_id: null,
        related_event_id: null,
      });
    }
  } catch (e) {
    console.warn('Failed to send block message to caterer', e);
  }
  return { success: true };
}

/** Unblock caterer: visible again to customers, caterer can use app. */
export async function unblockCaterer(catererId, adminUserId) {
  const { error } = await supabase
    .from('caterers')
    .update({
      is_blocked: false,
      blocked_at: null,
      blocked_reason: null,
      updated_at: new Date().toISOString()
    })
    .eq('id', catererId);

  if (error) {
    console.error('Failed to unblock caterer', error);
    throw error;
  }
  await logAdminActivity({
    adminUserId,
    activityType: 'caterer_unblocked',
    entityType: 'caterer',
    entityId: catererId,
    description: 'Caterer unblocked',
  });

  // Notify caterer via in-app message
  try {
    const { data: caterer } = await supabase
      .from('caterers')
      .select('user_id, business_name')
      .eq('id', catererId)
      .maybeSingle();

    if (caterer?.user_id && adminUserId) {
      const subject = 'Your Mrs Cater account has been unblocked';
      const body =
        `Hi ${caterer.business_name || 'there'},\n\n` +
        `Good news – the block on your Mrs Cater account has been lifted. ` +
        `You can now access your dashboard and continue serving customers.\n\n` +
        `— Mrs Cater Admin`;

      await supabase.from('messages').insert({
        sender_id: adminUserId,
        recipient_id: caterer.user_id,
        subject,
        body,
        related_order_id: null,
        related_event_id: null,
      });
    }
  } catch (e) {
    console.warn('Failed to send unblock message to caterer', e);
  }
  return { success: true };
}

export async function markCatererSuspect(catererId, reason, adminUserId) {
  const { error } = await supabase
    .from('caterers')
    .update({
      is_suspect: true,
      suspect_reason: reason || null,
      suspect_at: new Date().toISOString(),
      suspect_by: adminUserId || null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', catererId);
  if (error) {
    console.error('Failed to mark caterer as suspect', error);
    throw error;
  }
  await logAdminActivity({
    adminUserId,
    activityType: 'caterer_marked_suspect',
    entityType: 'caterer',
    entityId: catererId,
    description: 'Caterer flagged as suspect',
    metadata: { reason },
  });

  // Notify caterer via in-app message
  try {
    const { data: caterer } = await supabase
      .from('caterers')
      .select('user_id, business_name')
      .eq('id', catererId)
      .maybeSingle();

    if (caterer?.user_id && adminUserId) {
      const subject = 'Your account is under review';
      const body =
        `Hi ${caterer.business_name || 'there'},\n\n` +
        `Your Mrs Cater account has been flagged as "under review" by the admin team.\n\n` +
        `Reason: ${reason || 'No reason provided.'}\n\n` +
        `You can continue using the platform, but some actions may be limited while we investigate.\n\n` +
        `— Mrs Cater Admin`;

      await supabase.from('messages').insert({
        sender_id: adminUserId,
        recipient_id: caterer.user_id,
        subject,
        body,
        related_order_id: null,
        related_event_id: null,
      });
    }
  } catch (e) {
    console.warn('Failed to send suspect message to caterer', e);
  }
  return { success: true };
}

export async function clearCatererSuspect(catererId, adminUserId) {
  const { error } = await supabase
    .from('caterers')
    .update({
      is_suspect: false,
      suspect_reason: null,
      suspect_at: null,
      suspect_by: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', catererId);
  if (error) {
    console.error('Failed to clear suspect flag', error);
    throw error;
  }
  await logAdminActivity({
    adminUserId,
    activityType: 'caterer_suspect_cleared',
    entityType: 'caterer',
    entityId: catererId,
    description: 'Caterer suspect flag cleared',
  });

  // Notify caterer via in-app message
  try {
    const { data: caterer } = await supabase
      .from('caterers')
      .select('user_id, business_name')
      .eq('id', catererId)
      .maybeSingle();

    if (caterer?.user_id && adminUserId) {
      const subject = 'Your account review has been cleared';
      const body =
        `Hi ${caterer.business_name || 'there'},\n\n` +
        `The "under review" flag on your Mrs Cater account has been cleared.\n\n` +
        `Thank you for your cooperation.\n\n` +
        `— Mrs Cater Admin`;

      await supabase.from('messages').insert({
        sender_id: adminUserId,
        recipient_id: caterer.user_id,
        subject,
        body,
        related_order_id: null,
        related_event_id: null,
      });
    }
  } catch (e) {
    console.warn('Failed to send clear-suspect message to caterer', e);
  }
  return { success: true };
}

// ============================================================================
// CATERER PROFILE EDIT (admin)
// ============================================================================

/**
 * Upload a caterer image (logo or banner) to Supabase Storage and return the storage path.
 */
export async function uploadCatererImage(catererId, file, type) {
  if (!file || !catererId) throw new Error('Missing file or catererId');
  const bucket = 'profile-images';
  const safeType = type === 'banner' ? 'banner' : 'logo';
  const ext = file.name?.split('.').pop() || 'jpg';
  const path = `caterers/${catererId}/${safeType}-${Date.now()}.${ext}`;

  const { data, error } = await supabase.storage.from(bucket).upload(path, file, {
    upsert: true,
  });
  if (error) {
    console.error('Failed to upload caterer image', error);
    throw error;
  }
  // Prefer returning a public URL so admins can see/copy it easily when editing again.
  const uploadPath = data?.path || path;
  const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(uploadPath);
  return publicData?.publicUrl || uploadPath;
}

/**
 * Update core caterer profile fields as an admin.
 * Allows fixing imported data: business_name, email, phone, location, logo_url, hero_banner_url.
 */
export async function updateCatererProfile(catererId, updates) {
  const payload = {
    business_name: updates.business_name,
    email: updates.email,
    phone: updates.phone,
    location: updates.location,
    logo_url: updates.logo_url,
    hero_banner_url: updates.hero_banner_url,
    updated_at: new Date().toISOString(),
  };
  const { error } = await supabase
    .from('caterers')
    .update(payload)
    .eq('id', catererId);
  if (error) {
    console.error('Failed to update caterer profile', error);
    throw error;
  }
  await logAdminActivity({
    adminUserId: null,
    activityType: 'caterer_profile_updated',
    entityType: 'caterer',
    entityId: catererId,
    description: 'Caterer profile fields updated by admin',
    metadata: { fields: Object.keys(payload).filter((k) => updates[k] !== undefined) },
  });
  return { success: true };
}

// ============================================================================
// CATERER CLAIM REQUESTS
// ============================================================================

export async function fetchCatererClaimRequests({ status } = {}) {
  const buildBaseQuery = (selectClause) => {
    let query = supabase
      .from('business_claim_requests')
      .select(selectClause)
      .order('created_at', { ascending: false });

    if (status && status !== 'all') {
      query = query.eq('status', status);
    }
    return query;
  };

  // Preferred schema (with claim metadata on caterers)
  const primarySelect = `id, caterer_id, requester_user_id, requester_name, requester_email, requester_phone, message, status, created_at, reviewed_at, reviewed_by_user_id, decision_notes,
    caterer:caterers(id, business_name, email, claimed, claim_status)`;
  let { data, error } = await buildBaseQuery(primarySelect);

  if (!error) return data ?? [];

  const errorText = String(error?.message || error?.details || '').toLowerCase();
  const schemaMismatch =
    errorText.includes('column') ||
    errorText.includes('claim_status') ||
    errorText.includes('claimed') ||
    errorText.includes('relationship') ||
    errorText.includes('foreign key');

  // Backward-compatible fallback for databases where claim columns/FK metadata are not present yet
  if (schemaMismatch) {
    const fallbackSelect = `id, caterer_id, requester_user_id, requester_name, requester_email, requester_phone, message, status, created_at, reviewed_at, reviewed_by_user_id, decision_notes,
      caterer:caterers(id, business_name, email)`;
    const fallback = await buildBaseQuery(fallbackSelect);
    if (!fallback.error) {
      return (fallback.data || []).map((row) => ({
        ...row,
        caterer: row?.caterer
          ? { ...row.caterer, claimed: row.caterer.claimed ?? null, claim_status: row.caterer.claim_status ?? null }
          : row.caterer,
      }));
    }
  }

  const tableMissing =
    errorText.includes('business_claim_requests') &&
    (errorText.includes('does not exist') || errorText.includes('not found'));
  if (tableMissing) {
    throw new Error(
      'business_claim_requests table is missing in this database. Run the claim migration SQL and refresh.'
    );
  }

  console.error('Failed to load business claim requests', error);
  throw error;
}

export async function approveCatererClaimRequest(requestId, adminUserId) {
  const { data: req, error: loadError } = await supabase
    .from('business_claim_requests')
    .select('id, caterer_id, requester_user_id, requester_email, requester_name, created_at')
    .eq('id', requestId)
    .maybeSingle();
  if (loadError || !req) {
    throw loadError || new Error('Claim request not found');
  }

  const updates = {
    status: 'approved',
    reviewed_at: new Date().toISOString(),
    reviewed_by_user_id: adminUserId || null,
  };

  const { error: updateError } = await supabase
    .from('business_claim_requests')
    .update(updates)
    .eq('id', requestId);
  if (updateError) throw updateError;

  // Mark caterer as claimed; if requester_user_id is present, tie them as claimed_by_user_id.
  // Enforce one business per user (if we know the user at approval time)
  if (req.requester_user_id) {
    const { data: existingForUser, error: existingErr } = await supabase
      .from('caterers')
      .select('id')
      .eq('user_id', req.requester_user_id)
      .maybeSingle();

    if (existingErr) throw existingErr;
    if (existingForUser?.id && existingForUser.id !== req.caterer_id) {
      // Reject claim if claimant already owns another business
      await supabase
        .from('business_claim_requests')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
          reviewed_by_user_id: adminUserId || null,
          decision_notes: 'Rejected: one business per user (user already owns a different caterer).',
        })
        .eq('id', requestId);

      throw new Error('One business per user constraint: requester already owns another business.');
    }
  }

  const catererUpdate = {
    claimed: true,
    claim_status: 'claimed',
    claim_requested_at: req.created_at || null,
    claim_approved_at: new Date().toISOString(),
  };

  if (req.requester_user_id) {
    // Assign ownership immediately for logged-in claimants
    catererUpdate.user_id = req.requester_user_id;
    catererUpdate.claimed_by_user_id = req.requester_user_id;
  }

  const { error: catererError } = await supabase
    .from('caterers')
    .update(catererUpdate)
    .eq('id', req.caterer_id);
  if (catererError) {
    console.error('Failed to update caterer claim fields', catererError);
    throw catererError;
  }

  // Notify claimant (works for both guest and logged-in submissions)
  try {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
    if (SUPABASE_URL && req?.requester_email) {
      const { data: catererRow } = await supabase
        .from('caterers')
        .select('business_name')
        .eq('id', req.caterer_id)
        .maybeSingle();

      const businessName = catererRow?.business_name || 'your business';
      const maybeName = req?.requester_name ? ` ${req.requester_name}` : '';

      // Best-effort: never block approval because email failed.
      await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: req.requester_email,
          subject: `Claim approved: ${businessName}`,
          template: 'generic',
          data: {
            subject: `Claim approved: ${businessName}`,
            message: `Hi${maybeName},\n\nYour request to claim "${businessName}" on Mrs Cater has been approved by our admin team.\n\nTo finish setup, sign in to the Caterer Portal and continue your onboarding (use the same email you submitted in the claim form):\nhttps://caterer.mrscater.com/onboarding\n\nIf you already have a caterer account, you can sign in and you should see the business assigned automatically.\n`,
          },
        }),
      });
    }
  } catch (err) {
    console.warn('Failed to send claim approval email:', err?.message || err);
  }

  await logAdminActivity({
    adminUserId,
    activityType: 'caterer_claim_approved',
    entityType: 'business_claim_requests',
    entityId: requestId,
    description: 'Claim request approved',
    metadata: { caterer_id: req.caterer_id },
  });

  return { success: true };
}

export async function rejectCatererClaimRequest(requestId, adminUserId, decisionNotes) {
  const { error } = await supabase
    .from('business_claim_requests')
    .update({
      status: 'rejected',
      reviewed_at: new Date().toISOString(),
      reviewed_by_user_id: adminUserId || null,
      decision_notes: decisionNotes || null,
    })
    .eq('id', requestId);
  if (error) throw error;

  await logAdminActivity({
    adminUserId,
    activityType: 'caterer_claim_rejected',
    entityType: 'business_claim_requests',
    entityId: requestId,
    description: 'Claim request rejected',
    metadata: { decision_notes: decisionNotes },
  });
  return { success: true };
}

// ============================================================================
// ESCALATIONS
// ============================================================================

export async function fetchEscalations({ status, limit = 100, offset = 0 } = {}) {
  let query = supabase
    .from('admin_escalations')
    .select(`
      *,
      order:orders!admin_escalations_order_id_fkey(id, status, total_amount, delivery_date, caterer:caterers(business_name), customer:users(id, email))
    `)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  if (status && status !== 'all') {
    query = query.eq('status', status);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function createEscalation({ orderId, subject, notes, priority = 'medium' }, adminUserId) {
  const { data, error } = await supabase
    .from('admin_escalations')
    .insert({
      order_id: orderId,
      created_by: adminUserId,
      admin_user_id: adminUserId,
      subject: subject || null,
      notes: notes || null,
      priority,
      status: 'open',
    })
    .select()
    .single();
  if (error) throw error;
  await logAdminActivity({
    adminUserId,
    activityType: 'escalation_created',
    entityType: 'admin_escalations',
    entityId: data?.id,
    description: 'Escalation created',
    metadata: { order_id: orderId, priority },
  });
  return data;
}

export async function updateEscalationStatus(escalationId, status, resolution, resolvedBy) {
  const update = { status, updated_at: new Date().toISOString() };
  if (status === 'resolved' || status === 'closed') {
    update.resolved_at = new Date().toISOString();
    update.resolved_by = resolvedBy || null;
    if (resolution != null) update.resolution = resolution;
  }
  const { data, error } = await supabase
    .from('admin_escalations')
    .update(update)
    .eq('id', escalationId)
    .select()
    .single();
  if (error) throw error;
  await logAdminActivity({
    adminUserId: resolvedBy,
    activityType: 'escalation_updated',
    entityType: 'admin_escalations',
    entityId: escalationId,
    description: `Escalation status set to ${status}`,
    metadata: { status, resolution },
  });
  return data;
}

// ============================================================================
// COMPLIANCE REQUESTS
// ============================================================================

export async function fetchComplianceRequests({ status, limit = 100, offset = 0 } = {}) {
  let query = supabase
    .from('compliance_requests')
    .select(
      `id, caterer_id, business_registration, tax_id, operating_license, food_handling_cert, documents, status, reviewed_by, reviewed_at, rejection_reason, admin_notes, submitted_at, updated_at,
       caterer:caterers(id, business_name, email, verification_tier)`
    )
    .order('submitted_at', { ascending: false })
    .range(offset, offset + limit - 1);
  if (status && status !== 'all') {
    query = query.eq('status', status);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function updateComplianceRequest({ requestId, status, rejectionReason, adminNotes }, adminUserId) {
  const { data, error } = await supabase
    .from('compliance_requests')
    .update({
      status,
      rejection_reason: rejectionReason ?? null,
      admin_notes: adminNotes ?? null,
      reviewed_by: adminUserId ?? null,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', requestId)
    .select()
    .single();
  if (error) throw error;
  if (data?.caterer_id) {
    const complianceUpdate = {
      compliance_status: status,
      compliance_date: status === 'approved' ? new Date().toISOString() : null,
      compliance_rejection_reason: status === 'rejected' ? rejectionReason ?? null : null,
      updated_at: new Date().toISOString(),
    };
    const { error: catererError } = await supabase
      .from('caterers')
      .update(complianceUpdate)
      .eq('id', data.caterer_id);
    if (catererError) {
      console.error('Failed to update caterer compliance status', catererError);
    }

    // Notify caterer via in-app message about compliance decision
    try {
      const { data: caterer } = await supabase
        .from('caterers')
        .select('user_id, business_name')
        .eq('id', data.caterer_id)
        .maybeSingle();

      if (caterer?.user_id && adminUserId) {
        const subject =
          status === 'approved'
            ? 'Your compliance documents have been approved'
            : status === 'rejected'
            ? 'Your compliance documents were not approved'
            : 'Your compliance status has been updated';

        const reasonText =
          status === 'rejected'
            ? `\n\nReason: ${rejectionReason || 'No reason provided.'}`
            : '';

        const notesText =
          adminNotes
            ? `\n\nNotes from admin: ${adminNotes}`
            : '';

        const body =
          `Hi ${caterer.business_name || 'there'},\n\n` +
          `Your compliance request has been marked as "${status}".` +
          reasonText +
          notesText +
          `\n\nYou can view the status and, if rejected, resubmit updated documents from your Business Profile screen.\n\n` +
          `— Mrs Cater Admin`;

        await supabase.from('messages').insert({
          sender_id: adminUserId,
          recipient_id: caterer.user_id,
          subject,
          body,
          related_order_id: null,
          related_event_id: null,
        });
      }
    } catch (e) {
      console.warn('Failed to send compliance message to caterer', e);
    }
  }
  await logAdminActivity({
    adminUserId,
    activityType: 'compliance_request_updated',
    entityType: 'compliance_requests',
    entityId: requestId,
    description: `Compliance request marked ${status}`,
    metadata: { status, rejection_reason: rejectionReason },
  });
  return data;
}

// ============================================================================
// OBSERVABILITY LOGS
// ============================================================================

export async function fetchAppEventLogs({ app, level, search, limit = 200, offset = 0 } = {}) {
  let query = supabase
    .from('app_event_logs')
    .select('id, app_name, level, message, route, url, user_id, order_id, metadata, stack, created_at')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  if (app && app !== 'all') {
    query = query.eq('app_name', app);
  }
  if (level && level !== 'all') {
    query = query.eq('level', level);
  }
  if (search && search.trim()) {
    query = query.ilike('message', `%${search.trim()}%`);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function fetchAdminActivityLogs({ activityType, limit = 200, offset = 0 } = {}) {
  let query = supabase
    .from('admin_activity_logs')
    .select('id, admin_user_id, activity_type, entity_type, entity_id, description, metadata, created_at, admin:users!admin_activity_logs_admin_user_id_fkey(email)')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  if (activityType && activityType !== 'all') {
    query = query.eq('activity_type', activityType);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

/**
 * Send a platform notification to customers and/or caterers.
 * Appears in the notification panel of customer and caterer apps.
 */
export async function sendPlatformNotification({ title, body, recipientType, adminUserId }) {
  const valid = ['all', 'customers', 'caterers'].includes(recipientType);
  if (!valid || !title || !body?.trim()) {
    throw new Error('Invalid input: title and body required, recipientType must be all, customers, or caterers.');
  }
  const { data, error } = await supabase
    .from('platform_notifications')
    .insert({
      recipient_type: recipientType,
      title: String(title).trim().slice(0, 200),
      body: String(body).trim().slice(0, 2000),
      created_by: adminUserId || null,
      created_at: new Date().toISOString(),
    })
    .select('id')
    .single();
  if (error) throw error;
  return data;
}

// =============================================================================
// SUPPORT THREADS (customer inquiries; admin replies and marks resolved)
// =============================================================================

export async function fetchSupportThreads({ resolved = false, limit = 100 } = {}) {
  let query = supabase
    .from('support_threads')
    .select('id, customer_id, order_id, resolved_at, created_at, updated_at')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (resolved === true) {
    query = query.not('resolved_at', 'is', null);
  } else if (resolved === false) {
    query = query.is('resolved_at', null);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function fetchSupportThreadWithCustomer(threadId) {
  const { data, error } = await supabase
    .from('support_threads')
    .select('id, customer_id, order_id, resolved_at, created_at, updated_at')
    .eq('id', threadId)
    .single();
  if (error) throw error;
  const customerId = data?.customer_id;
  if (customerId) {
    const { data: profile } = await supabase.from('customer_profiles').select('full_name').eq('user_id', customerId).maybeSingle();
    data.customer_name = profile?.full_name || 'Customer';
  }
  return data;
}

export async function fetchSupportMessages(threadId) {
  const { data, error } = await supabase
    .from('support_messages')
    .select('*')
    .eq('thread_id', threadId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function sendSupportReply(threadId, body, adminUserId) {
  const { data, error } = await supabase
    .from('support_messages')
    .insert({
      thread_id: threadId,
      sender_id: adminUserId ?? null,
      sender_type: 'admin',
      body: String(body || '').trim(),
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function resolveSupportThread(threadId) {
  const { data, error } = await supabase
    .from('support_threads')
    .update({ resolved_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', threadId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Return count of open (unresolved) support threads for badges/notifications. */
export async function getOpenSupportThreadsCount() {
  const { count, error } = await supabase
    .from('support_threads')
    .select('*', { count: 'exact', head: true })
    .is('resolved_at', null);
  if (error) throw error;
  return count ?? 0;
}

/**
 * Subscribe to new support threads and new customer messages for admin notifications.
 * Calls onNewActivity() when a new thread is created or a customer sends a message.
 */
export function subscribeToSupportUpdates(onNewActivity) {
  const channel = supabase
    .channel('admin-support-updates')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'support_threads' },
      () => { onNewActivity?.({ type: 'thread' }); }
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'support_threads' },
      (payload) => {
        if (payload?.new?.resolved_at) onNewActivity?.({ type: 'resolved', threadId: payload.new?.id });
      }
    )
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'support_messages' },
      (payload) => {
        if (payload?.new?.sender_type === 'customer') {
          onNewActivity?.({ type: 'message', threadId: payload.new?.thread_id });
        }
      }
    )
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}

// =============================================================================
// ADMIN <-> CATERER DIRECT MESSAGES
// =============================================================================

export async function fetchConversationWithCaterer(adminUserId, catererUserId) {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .or(`and(sender_id.eq.${adminUserId},recipient_id.eq.${catererUserId}),and(sender_id.eq.${catererUserId},recipient_id.eq.${adminUserId})`)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function sendMessageToCaterer(adminUserId, catererUserId, body, subject = 'Message from Mrs Cater') {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      sender_id: adminUserId,
      recipient_id: catererUserId,
      subject: subject || 'Message from Mrs Cater',
      body: String(body || '').trim(),
      related_order_id: null,
      related_event_id: null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Subscribe to new messages to admin (when caterer responds, refetch if viewing that caterer) */
export function subscribeToAdminCatererMessages(adminUserId, catererUserId, onNewMessage) {
  const channel = supabase
    .channel(`admin-caterer-msg:${adminUserId}-${catererUserId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'messages', filter: `recipient_id=eq.${adminUserId}` },
      (payload) => {
        const m = payload?.new;
        if (m?.sender_id === catererUserId) onNewMessage?.();
      }
    )
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}
